import { dayjs } from '@convenia/utils'
import { is } from './is'

const isValid = (value) => dayjs(value).isValid()

export const MONTHS = {
  '01': 'janeiro',
  '02': 'fevereiro',
  '03': 'março',
  '04': 'abril',
  '05': 'maio',
  '06': 'junho',
  '07': 'julho',
  '08': 'agosto',
  '09': 'setembro',
  '10': 'outubro',
  '11': 'novembro',
  '12': 'dezembro'
}

export const toHours = (ms) => {
  const hours = Math.floor(ms / (1000 * 60 * 60))
  const minutes = Math.floor((ms / (1000 * 60)) % 60)
  const minutsStr = `${minutes}`.padStart(2, '0')

  return `${hours}:${minutsStr}`
}

/**
 * Formats a date to the brazilian date and time notation.
 * @deprecated AVOID USING THIS HELPER FOR NEW FEATURES - Use dayjs instead
 * e.g. dayjs().format('DD/MM/YYYY') / this.$date.format('DD/MM/YYYY')
 *
 * @param {String|Number|Date} value - The date either as an unix timestamp, a Date
 * instance, or a string following the ISO-8601 standard date format.
 */
export const formatDateBR = value => {
  if (!value) return ''

  // dayjs handles Number (timestamp), Date object, and ISO String automatically
  return dayjs(value).format('DD/MM/YYYY')
}

export const formatDateUS = date => {
  if (!date) return ''

  // if Date object, convert to America/Sao_Paulo timezone first
  if (is(date, 'Date')) {
    return dayjs(date).tz('America/Sao_Paulo').format('YYYY-MM-DD')
  }

  // if String in DD/MM/YYYY format, convert to YYYY-MM-DD
  return date.slice(0, 10).split('/').reverse().join('-')
}

/**
 * Receives a date string the format DD/MM/YYYY and returns
 * the correspodent timestamp for that date, note that we
 * have to manually set the time in here in the string
 * we receive from formatDateUS to avoid problems with
 * timezone.
 *
 * @param {String} date - The date string in the format DD/MM/YYYY
 * @returns {Number} Exact timestamp representation of given date string
 */
export const getTimestamp = date => (new Date(`${formatDateUS(date)}T00:00:00`)).getTime()

/**
 * Receives a date string in brazilian format and creates and returns
 * a new date object from that. Why? Because the native Date constructor
 * sucks.
 *
 * @param {String} value
 * @returns {Date} A new object corresponding to the given date string
 */
export const newDate = value => new Date(getTimestamp(value))

/**
 * Very simple method to check if a given date is valid
 *
 * @param {String} value - The date string in brazillian format only.
 * @returns {Boolean} Whether it is valid or not.
 */
export const isDateValid = value => newDate(value).toString() !== 'Invalid Date'

/**
 * Calculates the age given a Date
 * @param {Date} birthday
 * @returns {Number} - the age
 */
export const calculateAge = birthday => {
  const ageDifMs = Date.now() - birthday.getTime()
  const ageDate = new Date(ageDifMs)

  return Math.abs(ageDate.getUTCFullYear() - 1970)
}

/**
 * Normalizes the date, ignoring the user's timezone
 * converting the date to 'America/Sao_Paulo' timezone
 * and returning the date in 'YYYY-MM-DD' format
 *
 * @param {Date} date
 * @returns {String} - the normalized date
 */
export const normalizeDate = date => {
  return dayjs(date).tz('America/Sao_Paulo').format('YYYY-MM-DD')
}

/**
 * Extracts month and year of a given date string and returns it
 * in a nice legible string (used mostly for visual porpuses in some
 * components)
 *
 * @param {String} date - Date as a string in ISO-8601 format
 * @returns {String}
 */
export const getDateLabel = date => {
  if (!date) return ''
  const [ year, month, day ] = (date || '').split('-')
  return `${day} de ${MONTHS[month].toLowerCase()} de ${year}`
}

export const isAfter = ({ start, end }) => {
  if (!start || !isDateValid(start)) return true
  const startDate = getTimestamp(start)

  const endDate = (end && isDateValid(end)) ? getTimestamp(end) : +new Date()
  return startDate >= endDate
}

/**
 * Checks if a given date string is in the Brazilian date format (DD/MM/YYYY).
 *
 * @param {String} date - The date string to check.
 * @returns {Boolean} - True if the date is in the Brazilian format, false otherwise.
 */
export const isBrazilianDate = (date) => {
  if (typeof date !== 'string') return false

  const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/
  return dateRegex.test(date)
}

/**
 * Generates an array of time intervals between a specified start and end time.
 *
 * @param {string} start - The start time in HH:mm format. Defaults to '08:00'.
 * @param {string} end - The end time in HH:mm format. Defaults to '17:00'.
 * @param {number} intervalMinutes - The interval between each time in minutes. Defaults to 30.
 * @returns {Array<Object>} - An array of objects with each object containing a formatted time.
 *
 * @example
 *
 * generateTimeIntervals('08:00', '12:00', 60);
 * // Returns: [{ value: '08:00' }, { value: '09:00' }, ..., { value: '12:00' }]
 */
export const generateTimeIntervals = (start = '08:00', end = '17:00', intervalMinutes = 30) => {
  const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/
  if (!timeRegex.test(start)) throw new Error('Invalid start time format. Expected HH:mm.')
  if (!timeRegex.test(end)) throw new Error('Invalid end time format. Expected HH:mm.')

  const startTime = dayjs(start, 'HH:mm')
  const endTime = dayjs(end, 'HH:mm')
  const intervals = []

  for (let currentTime = startTime; currentTime.isBefore(endTime) || currentTime.isSame(endTime); currentTime = currentTime.add(intervalMinutes, 'minute')) {
    intervals.push({ value: currentTime.format('HH:mm') })
  }

  return intervals
}

/**
 * Return a formatted string from date.
 *
 * @param {Date|string} date - The date to format.
 * @param {string} format - The format pattern (YYYY-MM-DD by default).
 * @returns {string|null} String formatted from date if the date is valid, null otherwise.
 */
export const formatDateToString = (date, format = 'YYYY-MM-DD') => {
  if (!date) return null

  const possibleFormats = [
    'DD/MM/YYYY',
    'MM/DD/YYYY',
    'YYYY-MM-DD',
    'DD-MM-YYYY',
    'YYYY-MM-DD HH:mm:ss',
    'YYYY/MM/DD',
  ]

  if (date instanceof Date) {
    return dayjs(date).format(format)
  }

  const parsedDate = possibleFormats
    .map((formats) => dayjs(date, formats, true))
    .find((date) => date.isValid())

  return parsedDate ? parsedDate.format(format) : null
}

/**
 * Converts a date-like value to a valid Date object.
 *
 * @param {Date|string} value - Date value to parse.
 * @returns {Date|null} A Date instance when valid, otherwise null.
 */
export const toValidDate = (value) => {
  if (!value) return null

  const normalizedDate = formatDateToString(value, 'YYYY-MM-DD')
  if (!normalizedDate) return null

  const parsedDate = dayjs(normalizedDate, 'YYYY-MM-DD', true)

  return isValid(parsedDate) ? parsedDate.toDate() : null
}

/**
 * Creates a normalized date range object from start and end values.
 *
 * @param {Date|string} start - Start date value.
 * @param {Date|string} end - End date value.
 * @returns {{start: Date|null, end: Date|null}|null} Normalized range or null when
 * both dates are invalid.
 */
export const toDateRange = (start, end) => {
  const parsedStart = toValidDate(start)
  const parsedEnd = toValidDate(end)

  if (!parsedStart && !parsedEnd) return null

  return {
    start: parsedStart,
    end: parsedEnd
  }
}
